import { Box, Link, Text } from '@99designs/emu';
import { colors } from '@99designs/emu/settings';
import { useIsExperimentActivated } from '@99designs/experiments';
import { HowToSchema, HowToStep } from '@99designs/marketing-ui';
import { useTrackCTALinkClicked } from '../../../lib/events';
import { ProductLandingGroupKey } from '../../../lib/productLandingGroups';
import { removeHTMLTags } from '../../../util/removeHTMLTags';
import HowItWorkOptions from './HowItWorkOptions';

interface ContentProps {
    contestDescription: string;
    pricingLink: string;
    categoryKey: ProductLandingGroupKey;
    pricingOptions: string;
}
export default function HowItWorksContent({
    contestDescription,
    pricingLink,
    categoryKey,
    pricingOptions,
}: ContentProps) {
    const seePricingCTARef = useTrackCTALinkClicked({
        category: categoryKey,
        ctaLocation: 'how it works section',
        ctaButton: `See ${categoryKey} pricing options`,
    });
    const findADesignerCTARef = useTrackCTALinkClicked({
        category: categoryKey,
        ctaLocation: 'how it works section',
        ctaButton: 'Browse designer portfolios',
    });
    const isContestFocusEnabled =
        useIsExperimentActivated('contestCenteredHomepageHero', 'enabled') &&
        categoryKey === 'logo-design';
    const sectionName = __(
        'We offer two amazing ways to work with our community of professional designers.'
    );
    const contestHeading = __('Start a contest with our designer community');
    const hireDesignerHeading = __('Find a designer and start a 1-to-1 project');
    const hireDesignerDescription = __(
        'No matter your business needs, we’ve got an expert you can work with to bring your design to life.'
    );

    const stepsForSchema: HowToStep[] = [
        {
            name: contestHeading,
            description: removeHTMLTags(contestDescription),
        },
        {
            name: hireDesignerHeading,
            description: removeHTMLTags(hireDesignerDescription),
        },
    ];

    return isContestFocusEnabled ? (
        <Box maxWidth={['fit-content', 'fit-content', '450px', '500px']}>
            <Box>
                <Box display="flex" flexDirection={['column', 'row', 'column', 'column']}>
                    <HowItWorkOptions
                        badgeColor={colors.grey800}
                        badgeValue={1}
                        categoryKey="logo-design"
                        contentHeading={__placeholder('Tell us what you need')}
                        contentDescription={__placeholder(
                            'Our interactive creative brief is intuitive and simple. Describe your vision for the perfect logo design and then set your budget.'
                        )}
                    />
                    <HowItWorkOptions
                        badgeColor={colors.forrestLyf}
                        badgeValue={2}
                        categoryKey="logo-design"
                        contentHeading={__placeholder(
                            'Get dozens of options, unique to your brand'
                        )}
                        contentDescription={__placeholder(
                            'Professional logo designers from all over the world will send you options. You can give quick ratings and feedback to help them improve their submissions.'
                        )}
                    />
                    <HowItWorkOptions
                        badgeColor={colors.heritage}
                        badgeValue={3}
                        categoryKey="logo-design"
                        contentHeading={__placeholder('Choose a winning logo design')}
                        contentDescription={__placeholder(
                            "Select your favorite logo and we'll transfer the copyright, then send you the files to set you up for digital and print. Easy!"
                        )}
                    />
                </Box>
            </Box>
        </Box>
    ) : (
        <Box maxWidth={['fit-content', 'fit-content', '450px', '500px']}>
            <HowToSchema name={sectionName} steps={stepsForSchema} />
            <Text size="size4">{sectionName}</Text>
            <Box>
                <Box display="flex" flexDirection={['column', 'row', 'column', 'column']}>
                    <HowItWorkOptions
                        badgeColor={colors.sandyButt}
                        badgeValue={1}
                        contentHeading={contestHeading}
                        contentDescription={
                            <>
                                {contestDescription}
                                &nbsp;
                                <Link href={pricingLink} target="_blank" ref={seePricingCTARef}>
                                    {pricingOptions}
                                </Link>
                            </>
                        }
                    />
                    <HowItWorkOptions
                        badgeColor={colors.littlePiggy}
                        badgeValue={2}
                        contentHeading={hireDesignerHeading}
                        contentDescription={
                            <>
                                {hireDesignerDescription}
                                &nbsp;
                                <Link
                                    href={`/designers/search?categories[]=${categoryKey}`}
                                    target="_blank"
                                    ref={findADesignerCTARef}
                                >
                                    {__('Browse designer portfolios.')}
                                </Link>
                            </>
                        }
                    />
                </Box>
            </Box>
        </Box>
    );
}
